/**
 * One review queue for every staff Training subtab.
 *
 * A distinct proposal ID always remains reviewable, even if it maps to the
 * same course and source certificate as another proposal. Certificates are
 * evidence: never collapse different evidence IDs based on filename/date.
 * No verification/status changes happen in these read-only UI helpers.
 */
export const getTrainingReviewQueue = (proposedItems = []) => (
  (Array.isArray(proposedItems) ? proposedItems : [])
    .filter(item => item?.status === 'proposed')
);

// These are not approved qualifications and must remain visible for follow-up.
// Do not send them to the verify/reject action buttons until an authorised
// reviewer resolves their state through the supported evidence-review flow.
const FOLLOW_UP_STATUSES = new Set([
  'pending', 'pending_review', 'awaiting_review', 'needs_clarification',
]);

export const getTrainingFollowUpQueue = (items = []) => (
  (Array.isArray(items) ? items : [])
    .filter(item => FOLLOW_UP_STATUSES.has(String(item?.status || '').toLowerCase()))
);

export const getTrainingOpenItems = (items = []) => (
  (Array.isArray(items) ? items : []).filter(item =>
    item?.status === 'proposed' ||
    FOLLOW_UP_STATUSES.has(String(item?.status || '').toLowerCase())
  )
);

export const getCertificateEvidenceRows = (certificates = [], pendingItems = []) => {
  const pendingCountBySource = new Map();
  const followUpCountBySource = new Map();
  getTrainingReviewQueue(pendingItems).forEach(item => {
    if (!item?.source_document_id) return;
    const source = String(item.source_document_id);
    pendingCountBySource.set(source, (pendingCountBySource.get(source) || 0) + 1);
  });
  getTrainingFollowUpQueue(pendingItems).forEach(item => {
    if (!item?.source_document_id) return;
    const source = String(item.source_document_id);
    followUpCountBySource.set(source, (followUpCountBySource.get(source) || 0) + 1);
  });
  return (Array.isArray(certificates) ? certificates : []).map(cert => ({
    ...cert,
    pendingReviewCount: pendingCountBySource.get(String(cert.id)) || 0,
    followUpCount: followUpCountBySource.get(String(cert.id)) || 0,
  }));
};

export const getSourceReviewQueue = (pendingItems = [], sourceDocumentId = null) => (
  sourceDocumentId
    ? getTrainingReviewQueue(pendingItems).filter(
        item => String(item?.source_document_id || '') === String(sourceDocumentId)
      )
    : getTrainingReviewQueue(pendingItems)
);

/** Display duplicate subjects without dropping a single reviewable proposal. */
export const summarizeTrainingReviewQueue = (proposedItems = []) => {
  const queue = getTrainingReviewQueue(proposedItems);
  const sourceSubjectKeys = new Set(queue.map(item => JSON.stringify([
    String(item?.source_document_id || item?.id || ''),
    String(
      item?.mapped_training_code || item?.mapped_training_title ||
      item?.raw_course_title || item?.id || ''
    ).trim().toLowerCase(),
  ])));
  return {
    total: queue.length,
    sourceSubjectGroups: sourceSubjectKeys.size,
    repeatedEntries: queue.length - sourceSubjectKeys.size,
  };
};
